import React, { useEffect, useState } from 'react'
import { useAuthContext } from '../../auth/AuthContext'
import {
  CallRecordings,
  CallSummary,
  fetchCalls,
  fetchComposite,
  fetchRecordings,
  PagedCalls
} from '../../api/admin'
import { formatDateTime, formatDuration, hasAdminRole } from './adminUtils'

const PAGE_SIZE = 50

const styles: Record<string, React.CSSProperties> = {
  page: { padding: 24, fontFamily: 'sans-serif', height: '100vh', overflowY: 'auto', color: '#222' },
  layout: { display: 'flex', gap: 24, alignItems: 'flex-start' },
  list: { flex: 2, minWidth: 0 },
  detail: { flex: 1, position: 'sticky', top: 0, minWidth: 320 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th: { textAlign: 'left', borderBottom: '2px solid #ddd', padding: '8px 10px', whiteSpace: 'nowrap' },
  td: { borderBottom: '1px solid #eee', padding: '8px 10px', verticalAlign: 'top' },
  rowRec: { cursor: 'pointer' },
  badge: { fontSize: 12, padding: '2px 8px', borderRadius: 10, background: '#eef', whiteSpace: 'nowrap' },
  video: { width: '100%', background: '#000', borderRadius: 6 },
  pager: { display: 'flex', gap: 12, alignItems: 'center', marginTop: 16 }
}

const CallDetail: React.FC<{ token: string; call: CallSummary }> = ({ token, call }) => {
  const [rec, setRec] = useState<CallRecordings | null>(null)
  const [videoUrl, setVideoUrl] = useState<string | null>(null)
  const [building, setBuilding] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load recording metadata (this also kicks off server-side composite conversion).
  useEffect(() => {
    let cancelled = false
    setRec(null); setVideoUrl(null); setBuilding(false); setError(null)
    fetchRecordings(token, call.id)
      .then(r => { if (!cancelled) setRec(r) })
      .catch(() => { if (!cancelled) setError('Failed to load recordings') })
    return () => { cancelled = true }
  }, [token, call.id])

  // Poll for the composited video until it's ready.
  useEffect(() => {
    if (!rec || !call.has_recording) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout>
    const poll = async () => {
      try {
        const r = await fetchComposite(token, call.id)
        if (cancelled) return
        if (r.status === 'ready') { setBuilding(false); setVideoUrl(r.url) }
        else { setBuilding(true); timer = setTimeout(poll, 4000) }
      } catch {
        if (!cancelled) setError('Failed to load video')
      }
    }
    poll()
    return () => { cancelled = true; clearTimeout(timer) }
  }, [rec, token, call.id, call.has_recording])

  // Release the blob URL when it changes / on unmount.
  useEffect(() => () => { if (videoUrl) URL.revokeObjectURL(videoUrl) }, [videoUrl])

  return (
    <div style={styles.detail}>
      <h3>Call {call.id.slice(0, 8)}…</h3>
      <div style={{ fontSize: 13, color: '#555', marginBottom: 12 }}>
        {formatDateTime(call.created_date)} · {call.status} · {formatDuration(call.duration_seconds)}
      </div>

      {error && <div style={{ color: '#b00' }}>{error}</div>}

      {!call.has_recording && <div>No recording for this call.</div>}

      {call.has_recording && !videoUrl && !error &&
        <div>{building ? 'Preparing video… this can take a moment.' : 'Loading…'}</div>}

      {videoUrl && <video style={styles.video} src={videoUrl} controls autoPlay={false}/>}

      {rec && rec.sides.length > 0 &&
        <div style={{ marginTop: 12, fontSize: 13 }}>
          <strong>Participants</strong>
          <ul style={{ margin: '6px 0', paddingLeft: 18 }}>
            {rec.sides.map(s => (
              <li key={s.publisher_id}>
                {s.name ?? s.publisher_id.slice(0, 8)} — {s.is_interpreter ? 'Interpreter' : 'User'}
                {' '}({[s.has_video && 'video', s.has_audio && 'audio'].filter(Boolean).join(' + ') || 'no media'})
              </li>
            ))}
          </ul>
        </div>}
    </div>
  )
}

const AdminCallsPage: React.FC = () => {
  const { token, parsedToken } = useAuthContext()
  const roles = parsedToken()?.realm_access?.roles as string[] | undefined

  const [page, setPage] = useState<PagedCalls | null>(null)
  const [offset, setOffset] = useState(0)
  const [selected, setSelected] = useState<CallSummary | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!hasAdminRole(roles)) return
    let cancelled = false
    fetchCalls(token, { offset, limit: PAGE_SIZE })
      .then(p => { if (!cancelled) setPage(p) })
      .catch(() => { if (!cancelled) setError('Failed to load calls') })
    return () => { cancelled = true }
  }, [token, offset, roles])

  if (!hasAdminRole(roles)) {
    return <div style={styles.page}><h2>Not authorized</h2><p>This page requires the admin role.</p></div>
  }

  return (
    <div style={styles.page}>
      <h2>Call recordings</h2>
      {error && <div style={{ color: '#b00' }}>{error}</div>}
      <div style={styles.layout}>
        <div style={styles.list}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Duration</th>
                <th style={styles.th}>Participants</th>
                <th style={styles.th}>Recording</th>
              </tr>
            </thead>
            <tbody>
              {page?.items.map(c => (
                <tr key={c.id}
                    style={c.has_recording ? styles.rowRec : undefined}
                    onClick={() => c.has_recording && setSelected(c)}>
                  <td style={styles.td}>{formatDateTime(c.created_date)}</td>
                  <td style={styles.td}>{c.status}</td>
                  <td style={styles.td}>{formatDuration(c.duration_seconds)}</td>
                  <td style={styles.td}>
                    {c.participants.map(p => p.name ?? p.user_id.slice(0, 8)).join(', ') || '—'}
                  </td>
                  <td style={styles.td}>
                    {c.has_recording ? <span style={styles.badge}>▶ view</span> : '—'}
                  </td>
                </tr>
              ))}
              {page && page.items.length === 0 &&
                <tr><td style={styles.td} colSpan={5}>No calls found.</td></tr>}
            </tbody>
          </table>

          {page &&
            <div style={styles.pager}>
              <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Prev</button>
              <span>{offset + 1}–{Math.min(offset + PAGE_SIZE, page.total)} of {page.total}</span>
              <button disabled={offset + PAGE_SIZE >= page.total} onClick={() => setOffset(offset + PAGE_SIZE)}>Next</button>
            </div>}
        </div>

        {selected && <CallDetail token={token} call={selected}/>}
      </div>
    </div>
  )
}

export default AdminCallsPage
